export const JSONRPC_VERSION = "2.0";

export const METHOD_INIT = "init";
export const METHOD_READY = "ready";
export const METHOD_RENDER = "render";

export const MAX_SPEC_CHARS = 5_000_000;

// Application error code for a failed render (JSON-RPC reserves -32768..-32000).
export const ERROR_RENDER_FAILED = 1;

type Notification<Method extends string> = {
  jsonrpc: typeof JSONRPC_VERSION;
  method: Method;
};

export type InitNotification = Notification<typeof METHOD_INIT>;
export type ReadyNotification = Notification<typeof METHOD_READY>;

// id correlates a render with its result so the parent can ignore replies from
// a superseded render and the frame can drop stale work.
export type RenderRequest = {
  jsonrpc: typeof JSONRPC_VERSION;
  id: number;
  method: typeof METHOD_RENDER;
  params: { spec: unknown };
};

export type RenderResult = {
  jsonrpc: typeof JSONRPC_VERSION;
  id: number;
  result: null;
};

export type RenderError = {
  jsonrpc: typeof JSONRPC_VERSION;
  id: number;
  error: { code: number; message: string };
};

export type FromParent = InitNotification | RenderRequest;
export type FromFrame = ReadyNotification | RenderResult | RenderError;

const isRpc = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" &&
  value !== null &&
  (value as { jsonrpc?: unknown }).jsonrpc === JSONRPC_VERSION;

export const isInitNotification = (value: unknown): value is InitNotification =>
  isRpc(value) && value.method === METHOD_INIT;

export const isReadyNotification = (
  value: unknown,
): value is ReadyNotification => isRpc(value) && value.method === METHOD_READY;

export const isRenderRequest = (value: unknown): value is RenderRequest => {
  if (!isRpc(value)) return false;
  if (value.method !== METHOD_RENDER || typeof value.id !== "number") {
    return false;
  }
  const { params } = value;
  return (
    typeof params === "object" &&
    params !== null &&
    typeof (params as { spec?: unknown }).spec === "object" &&
    (params as { spec?: unknown }).spec !== null
  );
};

export const isRenderResult = (value: unknown): value is RenderResult =>
  isRpc(value) && typeof value.id === "number" && "result" in value;

export const isRenderError = (value: unknown): value is RenderError =>
  isRpc(value) && typeof value.id === "number" && "error" in value;
